<script setup lang="ts">
defineOptions({
	name: 'FormCode',
})

interface Props {
	errorMessage?: string
	codeLength?: number
	emailVerify?: string
	loading?: boolean
	disabledBtn?: boolean
	countdown?: number
	isCounting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	errorMessage: '',
	codeLength: 6,
	emailVerify: '',
	loading: false,
	disabledBtn: false,
	countdown: 0,
	isCounting: false,
})

const { t: $t } = useI18n()

const emit = defineEmits(['openLogin', 'resendEmail', 'submit', 'codeChange'])

const focusedIndex = ref(0)
const localErrorMessage = ref('')
const otp = ref<string[]>(Array.from({ length: props.codeLength }, () => ''))
const inputRefs = ref<HTMLInputElement[]>([])

const isCodeComplete = computed(() => otp.value.every((digit) => /^\d$/.test(digit)))
const codeValue = computed(() => otp.value.join(''))
const errorMessage = computed(() => props.errorMessage || localErrorMessage.value)
const hasError = computed(() => Boolean(errorMessage.value))

const isSubmitEnabled = computed(() => {
	return isCodeComplete.value && !props.loading && !props.disabledBtn
})

const handleSubmit = () => {
	if (!isCodeComplete.value || props.loading) return

	emit('submit', {
		code: codeValue.value,
	})
}

const setInputRef = (element: HTMLInputElement | null, index: number) => {
	if (!element) return
	inputRefs.value[index] = element
}

const getInputClass = (index: number) => {
	if (hasError.value) {
		return 'border-destructive-500 text-destructive-400 caret-destructive-500'
	}

	if (otp.value[index] || focusedIndex.value === index) {
		return 'border-primary-500 text-neutral-50 caret-primary-500'
	}

	return 'border-[#56607B] text-neutral-50 caret-primary-500'
}

const fillOtp = async (digits: string, startIndex = 0) => {
	const cleanDigits = digits.replace(/\D/g, '').slice(0, props.codeLength - startIndex)
	if (!cleanDigits) return

	localErrorMessage.value = ''

	for (const [offset, digit] of Array.from(cleanDigits).entries()) {
		otp.value[startIndex + offset] = digit
	}

	const nextIndex = Math.min(startIndex + cleanDigits.length, props.codeLength - 1)
	await focusInput(nextIndex)
}

const handleInput = async (event: Event, index: number) => {
	const target = event.target as HTMLInputElement
	const digits = target.value.replace(/\D/g, '')

	if (!digits) {
		otp.value[index] = ''
		return
	}

	if (digits.length > 1) {
		await fillOtp(digits, index)
		return
	}

	localErrorMessage.value = ''
	otp.value[index] = digits

	if (index < props.codeLength - 1) {
		await focusInput(index + 1)
	}
}

const focusInput = async (index: number) => {
	await nextTick()
	const safeIndex = Math.min(Math.max(index, 0), props.codeLength - 1)
	const element = inputRefs.value[safeIndex]
	if (!element) return
	focusedIndex.value = safeIndex
	element.focus()
	element.select()
}

const handleKeydown = async (event: KeyboardEvent, index: number) => {
	const allowedControlKeys = ['Tab', 'Shift', 'Meta', 'Control', 'Alt']
	if (allowedControlKeys.includes(event.key)) return

	// Permitir atajos del sistema: Ctrl/Cmd + V, C, X, A, etc.
	if (event.ctrlKey || event.metaKey) return

	// Permitir también Shift + Insert para pegar en algunos entornos
	if (event.shiftKey && event.key === 'Insert') return

	if (event.key === 'ArrowLeft') {
		event.preventDefault()
		await focusInput(index - 1)
		return
	}

	if (event.key === 'ArrowRight') {
		event.preventDefault()
		await focusInput(index + 1)
		return
	}

	if (event.key === 'Backspace') {
		event.preventDefault()
		localErrorMessage.value = ''

		if (otp.value[index]) {
			otp.value[index] = ''
			return
		}

		if (index > 0) {
			otp.value[index - 1] = ''
			await focusInput(index - 1)
		}
		return
	}

	if (!/^\d$/.test(event.key)) {
		event.preventDefault()
	}
}

const handlePaste = async (event: ClipboardEvent, index: number) => {
	event.preventDefault()
	const pastedText = event.clipboardData?.getData('text') ?? ''
	await fillOtp(pastedText, index)
}

watch(
	() => otp.value.join(''),
	(newVal) => {
		if (newVal) {
			emit('codeChange', newVal)
		}
	},
)
</script>

<template>
	<form
		class="space-y-8 md:space-y-16"
		@submit.prevent="handleSubmit"
	>
		<div class="space-y-4 px-4 smm:px-12">
			<div class="flex flex-wrap justify-center gap-3">
				<input
					v-for="(_, index) in otp"
					:key="index"
					:ref="(element) => setInputRef(element as HTMLInputElement | null, index)"
					v-model="otp[index]"
					type="text"
					inputmode="numeric"
					autocomplete="one-time-code"
					pattern="[0-9]*"
					maxlength="1"
					class="h-12 w-11 rounded-lg border bg-transparent text-center text-lg font-semibold outline-none transition-colors"
					:class="getInputClass(index)"
					@focus="focusedIndex = index"
					@input="handleInput($event, index)"
					@keydown="handleKeydown($event, index)"
					@paste="handlePaste($event, index)"
				/>
			</div>

			<p
				v-if="hasError"
				class="text-sm text-destructive-500 text-center"
			>
				{{ $t(errorMessage) }}.
			</p>
		</div>

		<div class="space-y-4 text-center px-4 smm:px-12">
			<p class="text-neutral-50">{{ $t("Didn't receive the code?") }}</p>
			<p
				v-if="props.isCounting"
				class="text-base text-white/60"
			>
				{{ props.countdown }}s {{ $t('to resend') }}
			</p>
			<UButton
				v-else
				type="button"
				color="primary"
				variant="link"
				class="mx-auto p-0 text-base text-primary-500 underline underline-offset-4 hover:text-primary-400"
				@click="emit('resendEmail')"
			>
				{{ $t('Resend code') }}
			</UButton>
		</div>

		<div class="flex flex-col gap-4 smm:flex-row smm:justify-center items-center px-4 smm:px-0">
			<UButton
				type="button"
				color="white"
				variant="outline"
				:label="$t('Back to Login')"
				class="rounded-full text-neutral-50 text-base font-semibold py-2 px-5 w-full smm:w-auto smm:min-w-44 justify-center hover:bg-neutral-50/30"
				@click="emit('openLogin')"
			/>
			<UButton
				type="submit"
				:loading="props.loading"
				:disabled="!isSubmitEnabled"
				:label="$t('Confirm')"
				color="white"
				class="rounded-full text-neutral-50 text-base font-semibold py-2 px-5 w-full smm:w-auto smm:min-w-44 justify-center hover:bg-neutral-50/30"
				:class="
					isSubmitEnabled
						? 'bg-primary-500 ring-primary-500 text-white hover:bg-primary-600'
						: '!bg-secondary-600 text-neutral-700 !ring-secondary-600'
				"
			/>
		</div>
	</form>
</template>
